import { ChevronDownFilled } from '@fluentui/react-icons';
import classNames from 'classnames';
import { useAtom } from 'jotai';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  usePause,
  usePauseReasons,
  usePauseStatus,
  useUnpause,
} from '@/api/pauses';
import { useExtensionPermissions } from '@/api/use-extension-permissions';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { defaultPauseReasons } from '@/config/default-pause-reasons';
import useDuration from '@/hooks/use-duration';
import { useExtension } from '@/hooks/use-extension';
import { notifyDialogAtom } from '@/store/atoms';
import { openWidget } from '@/utils/open-widget';

const Status = () => {
  const { t } = useTranslation('status');

  const extension = useExtension();
  const { data: permissions } = useExtensionPermissions();
  const showPauseAlert = permissions?.showPauseAlert ?? true;
  const { data: pauseStatus, isPending: pauseStatusIsPending } =
    usePauseStatus();
  const { data: pauseReasons } = usePauseReasons();
  const {
    mutate: pause,
    isPending: isPausingPending,
    variables: pauseVariables,
    submittedAt: pauseSubmittedAt,
  } = usePause();
  const {
    mutate: unpause,
    isPending: isUnpausingPending,
    submittedAt: unpauseSubmittedAt,
  } = useUnpause();

  const isPausing = isPausingPending && pauseSubmittedAt > unpauseSubmittedAt;
  const isUnpausing =
    isUnpausingPending && unpauseSubmittedAt > pauseSubmittedAt;

  const pauseReasonCode = isUnpausing
    ? undefined
    : isPausing
      ? pauseVariables.reasonCode
      : pauseStatus?.code;
  const pauseReason = pauseReasons?.find(
    (reason) => reason.pauseReasonCode === pauseReasonCode
  );

  const pauseName =
    pauseReason?.pauseReasonName || pauseReasonCode || undefined;

  const onPause = (code: string) => () => {
    if (!extension?.number || code === pauseReasonCode) return;
    pause({ extension: extension.number, reasonCode: code });
  };

  const onUnpause = () => {
    if (
      !extension?.number ||
      (pauseStatusIsPending ? isUnpausing : !pauseReasonCode)
    ) {
      return;
    }

    unpause(extension.number);
  };

  const [notifyDialog, setNotifyDialog] = useAtom(notifyDialogAtom);

  const onUnpauseWithDialog = () => {
    onUnpause();
    setNotifyDialog({
      open: false,
    });
  };

  const pauseDuration = useDuration(
    isPausing ? new Date(pauseSubmittedAt) : pauseStatus?.date || new Date()
  );
  const [minutes, seconds] = pauseDuration.split(':').map(Number);

  useEffect(() => {
    if (!showPauseAlert) return;

    if (
      !pauseReason ||
      minutes === undefined ||
      seconds === undefined ||
      (minutes === 0 && seconds === 0)
    ) {
      return;
    }

    if (seconds === 0 && minutes % pauseReason.duration === 0) {
      setNotifyDialog({
        open: true,
      });
      openWidget();
    }
  }, [pauseDuration, pauseReason, minutes, seconds, setNotifyDialog, showPauseAlert]);

  useEffect(() => {
    localStorage.setItem('missedCalls', '0');
  }, [pauseReasonCode]);

  const onNotifyDialogOpenChange = (open: boolean) => {
    setNotifyDialog({
      open,
    });
  };

  const [isStatusDropdownListOpen, setIsStatusDropdownListOpen] =
    useState(false);

  const isDesktopDialog =
    typeof notifyDialog.message === 'string' &&
    notifyDialog.message.startsWith('desktop');

  const dialogTitle = isDesktopDialog ? t('Atenção') : t('Aviso de pausa');

  const shouldRenderNotifyDialog =
    Boolean(notifyDialog.message) || showPauseAlert;

  return (
    <>
      {shouldRenderNotifyDialog ? (
        <Dialog open={notifyDialog.open} onOpenChange={onNotifyDialogOpenChange}>
          <DialogContent className="w-[90%]">
            <DialogHeader>
              <DialogTitle>{dialogTitle}!</DialogTitle>
              <DialogDescription className="text-left">
                {notifyDialog.message ? (
                  t(notifyDialog.message)
                ) : (
                  <>
                    {t('Você está em pausa há') + ' '}
                    <span className="font-semibold">
                      {minutes} {minutes === 1 ? t('minuto') : t('minutos')}
                      {' ' + t('e') + ' '}
                      {seconds} {seconds === 1 ? t('segundo') : t('segundos')}.{' '}
                    </span>
                    {t('Motivo da pausa') + ': '}
                    <span className="font-semibold">
                      {pauseName && t(pauseName)}
                    </span>
                    .
                  </>
                )}
              </DialogDescription>
            </DialogHeader>
            {!isDesktopDialog && (
              <DialogFooter>
                <Button
                  className="bg-green-600 hover:bg-green-700 active:bg-green-900"
                  onClick={onUnpauseWithDialog}
                >
                  {t('Ficar disponível')}
                </Button>
              </DialogFooter>
            )}
          </DialogContent>
        </Dialog>
      ) : null}

      <div className="flex h-8 min-h-8 items-center justify-between space-x-2 border-b border-[#c8c8c8] bg-[#fafafa] pl-5 pr-2">
        <div className="flex items-center space-x-4 overflow-hidden">
          <div
            className={classNames('aspect-square h-2 rounded-full', {
              ['bg-green-600']: pauseStatusIsPending
                ? isUnpausing
                : !pauseReasonCode,
              ['bg-red-500']: pauseReasonCode,
              ['bg-gray-300']:
                pauseStatusIsPending && !isPausing && !isUnpausing,
            })}
          />
          <p
            title={pauseName && t(pauseName)}
            className="flex select-none space-x-2 text-xs font-semibold text-[#585858]"
          >
            {pauseName ? (
              <>
                <span className="max-w-[67%] overflow-hidden text-ellipsis whitespace-nowrap">
                  {t(pauseName)}
                </span>
                <span>{`(${pauseDuration})`}</span>
              </>
            ) : isUnpausing ? (
              <>{t('Disponível')}</>
            ) : pauseStatusIsPending ? (
              <>{t('loading')}</>
            ) : (
              <>{t('Disponível')}</>
            )}
          </p>
        </div>

        <DropdownMenu
          open={isStatusDropdownListOpen}
          onOpenChange={(isOpen) => setIsStatusDropdownListOpen(isOpen)}
        >
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-[80%] transition-all hover:bg-[#ececec] active:bg-[#dddddd]"
            >
              <ChevronDownFilled
                color="#5d5d5d"
                className={classNames(
                  { ['-rotate-90']: !isStatusDropdownListOpen },
                  'transition-all'
                )}
              />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-40">
            <DropdownMenuGroup>
              <DropdownMenuLabel className="select-none">
                Status
              </DropdownMenuLabel>
              <DropdownMenuItem
                className="cursor-pointer transition-all hover:bg-[#ececec] active:bg-[#dddddd]"
                onClick={onUnpause}
              >
                <div className="aspect-square h-2 rounded-full bg-green-600" />
                <span className="ml-2">{t('Disponível')}</span>
              </DropdownMenuItem>
              <DropdownMenuLabel className="select-none">
                {t('Pausas')}
              </DropdownMenuLabel>
              {pauseReasons
                .filter(
                  ({ pauseReasonCode }) =>
                    pauseReasonCode !== defaultPauseReasons.LOGIN &&
                    pauseReasonCode !== defaultPauseReasons.NOT_ANSWERED_PAUSE
                )
                .map(({ pauseReasonName, pauseReasonCode }) => (
                  <DropdownMenuItem
                    key={pauseReasonCode}
                    className="cursor-pointer transition-all hover:bg-[#ececec] active:bg-[#dddddd]"
                    onClick={onPause(pauseReasonCode)}
                    title={pauseReasonName}
                  >
                    <div className="aspect-square h-2 rounded-full bg-red-500" />
                    <span className="ml-2 overflow-hidden text-ellipsis whitespace-nowrap">
                      {pauseReasonName}
                    </span>
                  </DropdownMenuItem>
                ))}
            </DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </>
  );
};

export default Status;
